/**
 * The `servicenow_incident` agent-action view capability — the render half
 * of the ServiceNow create-incident approval card. Holds the
 * ServiceNow-specific presentation (the "create" badge, the proposed
 * incident body, and the created/rejected settled summary) that the generic
 * `AgentActionApprovalPanel` delegates to. Registered as a side effect on
 * import.
 */
import type { InFlightAgentActionProposal } from '@axilon-apps/domains-ontology-ui-state'
import { registerAgentActionView, type AgentActionViewCapability } from './viewRegistry'

type ServicenowIncidentProposal = Extract<
  InFlightAgentActionProposal,
  { kind: 'servicenow_incident' }
>

function isServicenowIncident(p: InFlightAgentActionProposal): p is ServicenowIncidentProposal {
  return p.kind === 'servicenow_incident'
}

/** Pretty-print a snake_case ServiceNow field key for the card body. */
function fieldLabel(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function ProposalDetail({ proposal }: { proposal: ServicenowIncidentProposal }) {
  const fieldEntries = Object.entries(proposal.fields ?? {})
  return (
    <div className="rounded-xl border border-line/70 bg-muted/30 p-2 text-11 text-foreground">
      <div className="grid gap-1.5">
        <div className="break-words">
          <span className="text-muted-foreground">Summary:</span> {proposal.shortDescription}
        </div>
        {proposal.integrationId ? (
          <div className="break-words">
            <span className="text-muted-foreground">Integration:</span> {proposal.integrationId}
          </div>
        ) : null}
        {fieldEntries.map(([key, value]) => (
          <div key={key} className="break-words">
            <span className="text-muted-foreground">{fieldLabel(key)}:</span> {value}
          </div>
        ))}
      </div>
    </div>
  )
}

function SettledRowDetail({ proposal }: { proposal: ServicenowIncidentProposal }) {
  const terminal = proposal.terminalState
  if (!terminal) return null
  if (terminal.kind === 'applied') {
    const created = proposal.createdIncident
    const ref = created?.number ?? created?.sysId
    return (
      <p className="px-1 text-10 text-emerald-600 dark:text-emerald-400">
        Created{ref ? ` — ${ref}` : ''}.
      </p>
    )
  }
  if (terminal.kind === 'apply_failed') {
    return <p className="px-1 text-10 text-destructive">Create failed — {terminal.error}</p>
  }
  return (
    <p className="px-1 text-10 text-muted-foreground">
      Rejected{proposal.rejectionReason ? ` — ${proposal.rejectionReason}` : ''}.
    </p>
  )
}

export const servicenowIncidentViewCapability: AgentActionViewCapability = {
  kind: 'servicenow_incident',
  badgeLabel() {
    return 'incident'
  },
  renderDetail(proposal) {
    if (!isServicenowIncident(proposal)) return null
    return <ProposalDetail proposal={proposal} />
  },
  renderSettledSummary(proposal) {
    if (!isServicenowIncident(proposal)) return null
    return <SettledRowDetail proposal={proposal} />
  }
}

registerAgentActionView(servicenowIncidentViewCapability)
